import { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ClockIcon,
  ArrowPathIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  EyeIcon,
} from '@heroicons/react/24/outline'
import { Button } from 'src/components/button'
import { WithTooltip } from 'src/components/tooltip'
import { useAsyncJobStore, useUnacknowledgedFailures } from 'src/stores/async-jobs'
import { asyncJobApi } from 'src/services/async-jobs/api'
import { asyncJobQueries } from 'src/services/async-jobs/queries'
import { AsyncJob, jobStatusEnum } from 'src/services/async-jobs/models'
import { getTaskTypeDisplayName, resolveTaskType } from 'src/services/async-jobs/task-type-registry'
import { useToast } from 'src/components/toast'
import { formatDistanceToNow } from 'date-fns'

function useElapsedTime(dateString: string | null) {
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!dateString) return
    const interval = setInterval(() => setTick((t) => t + 1), 1000)
    return () => clearInterval(interval)
  }, [dateString])
  if (!dateString) return null
  return formatDistanceToNow(new Date(dateString), { includeSeconds: true })
}

function ElapsedTime({ job }: { job: AsyncJob }) {
  const dateStr = job.startedAt ?? job.created
  const elapsed = useElapsedTime(dateStr)
  if (!elapsed) return null
  return <span className="text-xs text-text-tertiary">{elapsed}</span>
}

function StatusIcon({ status }: { status: string }) {
  if (status === jobStatusEnum.QUEUED) {
    return <ClockIcon className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
  }
  if (status === jobStatusEnum.IN_PROGRESS) {
    return <ArrowPathIcon className="h-4 w-4 animate-spin text-primary-400" aria-hidden="true" />
  }
  return null
}

function ActiveJobRow({ job, onCancel }: { job: AsyncJob; onCancel: (job: AsyncJob) => void }) {
  const { t } = useTranslation()
  return (
    <div className="flex items-center gap-2 px-3 py-2 text-sm">
      <StatusIcon status={job.status} />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-text-primary">{job.label}</p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-placeholder">
            {getTaskTypeDisplayName(job.taskType)}
          </span>
          <span className="text-gray-300">·</span>
          <ElapsedTime job={job} />
        </div>
      </div>
      {job.status === jobStatusEnum.QUEUED && (
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          iconOnly
          shape="square"
          tooltip={t('asyncJobs.cancelTask')}
          onClick={() => onCancel(job)}
          className="shrink-0"
        >
          <XMarkIcon className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
    </div>
  )
}

function FailedJobRow({ job, onDismiss }: { job: AsyncJob; onDismiss: (id: string) => void }) {
  const { t } = useTranslation()
  const { href } = resolveTaskType(job)

  return (
    <div className="flex items-start gap-2 px-3 py-2 text-sm">
      <ExclamationTriangleIcon
        className="mt-0.5 h-4 w-4 shrink-0 text-error-400"
        aria-hidden="true"
      />
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-text-primary">{job.label}</p>
        <span className="text-xs text-text-placeholder">
          {getTaskTypeDisplayName(job.taskType)}
        </span>
        {job.errorMessage && (
          <WithTooltip label={job.errorMessage}>
            <p className="mt-0.5 line-clamp-2 text-xs text-error-600">{job.errorMessage}</p>
          </WithTooltip>
        )}
      </div>
      {href && (
        <Button
          variant="ghost"
          tone="neutral"
          iconOnly
          size="sm"
          shape="square"
          tooltip={t('asyncJobs.viewTask')}
          link={{ to: href, className: 'shrink-0' }}
        >
          <EyeIcon className="h-4 w-4" aria-hidden="true" />
        </Button>
      )}
      <Button
        variant="ghost"
        tone="neutral"
        iconOnly
        size="sm"
        shape="square"
        onClick={() => onDismiss(job.id)}
        className="shrink-0"
        tooltip={t('common.dismiss')}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  )
}

/**
 * The active-tasks list body: an "Active" section (per-job cancel) and a
 * "Failed" section (per-job dismiss + dismiss-all), preserving the floating
 * widget's behavior. Shared by the side-nav alerts flyout. Renders an empty
 * state when there is nothing to show so hosts can always open it.
 */
export const ActiveTasksList = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const active = useAsyncJobStore.use.active()
  const unacknowledgedFailures = useUnacknowledgedFailures()
  const { acknowledgeJob, removeFromActive, restoreActive, pushRecent } = useAsyncJobStore(
    (s) => s.actions,
  )

  const sortedActive = [...active].sort(
    (a, b) => new Date(a.created).getTime() - new Date(b.created).getTime(),
  )

  const sortedFailed = [...unacknowledgedFailures].sort(
    (a, b) =>
      new Date(b.completedAt ?? b.created).getTime() -
      new Date(a.completedAt ?? a.created).getTime(),
  )

  const cancelMutation = useMutation({
    mutationFn: (jobId: string) => asyncJobApi.csc.cancel(jobId),
    onMutate: async (jobId) => {
      const jobToCancel = active.find((j) => j.id === jobId)
      if (jobToCancel) {
        removeFromActive(jobId)
      }
      return { jobToCancel }
    },
    onSuccess: (cancelledJob) => {
      pushRecent(cancelledJob)
      queryClient.invalidateQueries({ queryKey: asyncJobQueries.all() })
      toast({ description: t('asyncJobs.cancelled', { label: cancelledJob.label }) })
    },
    onError: (error, _jobId, context) => {
      if (context?.jobToCancel) {
        restoreActive(context.jobToCancel)
      }
      const is409 =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { status?: number } }).response?.status === 409
          : false
      if (is409) {
        toast({
          title: t('asyncJobs.cannotCancel'),
          description: t('asyncJobs.tooLate'),
          type: 'error',
        })
      } else {
        toast({
          title: t('asyncJobs.cancelFailed'),
          description: t('asyncJobs.couldNotCancel'),
          type: 'error',
        })
      }
    },
  })

  const handleCancel = useCallback(
    (job: AsyncJob) => {
      cancelMutation.mutate(job.id)
    },
    [cancelMutation],
  )

  const handleDismiss = useCallback(
    (id: string) => {
      acknowledgeJob(id)
    },
    [acknowledgeJob],
  )

  const handleDismissAll = useCallback(() => {
    for (const job of unacknowledgedFailures) {
      acknowledgeJob(job.id)
    }
  }, [unacknowledgedFailures, acknowledgeJob])

  const isEmpty = sortedActive.length === 0 && sortedFailed.length === 0

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-xl">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-text-primary">{t('asyncJobs.title')}</h3>
      </div>

      {isEmpty ? (
        <div className="px-4 py-8 text-center">
          <ClockIcon className="mx-auto h-8 w-8 text-gray-300" aria-hidden="true" />
          <p className="mt-2 text-sm text-text-placeholder">{t('asyncJobs.empty')}</p>
        </div>
      ) : (
        <>
          {sortedActive.length > 0 && (
            <div>
              <div className="border-b border-border bg-surface-hover px-3 py-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-text-placeholder">
                  {t('asyncJobs.activeSection')}
                </h4>
              </div>
              <div className="max-h-48 divide-y divide-border overflow-y-auto">
                {sortedActive.map((job) => (
                  <ActiveJobRow key={job.id} job={job} onCancel={handleCancel} />
                ))}
              </div>
            </div>
          )}

          {sortedFailed.length > 0 && (
            <div>
              <div className="flex items-center justify-between border-b border-border bg-error-50 px-3 py-1.5">
                <h4 className="text-xs font-semibold uppercase tracking-wider text-error-600">
                  {t('asyncJobs.failedSection')}
                </h4>
                {sortedFailed.length > 1 && (
                  <Button
                    variant="text"
                    tone="error"
                    onClick={handleDismissAll}
                    className="text-xs font-medium"
                  >
                    {t('asyncJobs.dismissAll')}
                  </Button>
                )}
              </div>
              <div className="max-h-48 divide-y divide-border overflow-y-auto">
                {sortedFailed.map((job) => (
                  <FailedJobRow key={job.id} job={job} onDismiss={handleDismiss} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
